import { STOCK_TRANSFER_WORKFLOW_TYPE_IDS } from './stock-transfer-workflow.constants';

export { STOCK_TRANSFER_WORKFLOW_TYPE_IDS };

/** Bon de transfert dupliqué depuis un PV d'écart validé (`type_initial` = PV ECART). */
export function isBonTransfertDocumentFromPvEcart(
    doc:
        | {
              type_document_id?: number | null;
              type_initial?: number | null;
              typeInitial?: number | null;
              note?: string | null;
          }
        | null
        | undefined,
    documentTypeLabel?: string | null
): boolean {
    const label = String(documentTypeLabel ?? '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .trim();
    const isBt =
        label === 'BON TRANSFERT' ||
        label === 'TRANSFERT' ||
        Number(doc?.type_document_id) === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT;
    if (!isBt) {
        return false;
    }
    const typeInitial = Number(doc?.type_initial ?? doc?.typeInitial ?? 0);
    if (typeInitial === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART) {
        return true;
    }
    const note = String(doc?.note ?? '').toLowerCase();
    return (
        note.includes("pv d'écart") ||
        note.includes('pv d’écart') ||
        note.includes('pv decart')
    );
}

/**
 * Workflow gestion stock (aligné ERP + LC) :
 *
 * TRANSFERT ENTREPÔT → validation/envoi → BON DE TRANSFERT → BON DE SORTIE
 * → EN CIRCULATION → BON DE RÉCEPTION → Soldé | reste en circulation | PV d'écart
 */
export const LC_STOCK_TRANSFERT_IS_RECEPTION = {
  DEFAUT: 0,
  RECEPTION_TERMINEE: 1,
  EN_COURS_TRANSFERT: 2,
  ANNULE: 3,
  EN_COURS_LIVRAISON: 4,
} as const;

export type LcStockTransfertTab =
  | 'tous'
  | 'en_cours'
  | 'livraison'
  | 'reception'
  | 'clos';

/** Libellé phase LC affiché (liste, détail, modale). */
export function getLcTransfertWorkflowPhaseLabel(
  isReceptionCode: unknown,
  circulationCree?: unknown
): string {
  if (
    Number(isReceptionCode) === LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_LIVRAISON &&
    (circulationCree === true || Number(circulationCree) === 1)
  ) {
    return 'En circulation';
  }
  switch (Number(isReceptionCode)) {
    case LC_STOCK_TRANSFERT_IS_RECEPTION.DEFAUT:
      return 'Brouillon';
    case LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_TRANSFERT:
      return 'Créé';
    case LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_LIVRAISON:
      return 'Validé';
    case LC_STOCK_TRANSFERT_IS_RECEPTION.RECEPTION_TERMINEE:
      return 'Bon réception';
    case LC_STOCK_TRANSFERT_IS_RECEPTION.ANNULE:
      return 'Annulé';
    default:
      return Number.isFinite(Number(isReceptionCode)) ? String(isReceptionCode) : '—';
  }
}

/** Sous-titre métier pour une phase LC (info utilisateur). */
export function getLcTransfertWorkflowPhaseHint(isReceptionCode: unknown): string {
  switch (Number(isReceptionCode)) {
    case LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_TRANSFERT:
    case LC_STOCK_TRANSFERT_IS_RECEPTION.DEFAUT:
      return 'Saisie / validation — génère Bon de transfert et Bon de sortie';
    case LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_LIVRAISON:
      return 'Stock intermédiaire — en attente de Bon de réception';
    case LC_STOCK_TRANSFERT_IS_RECEPTION.RECEPTION_TERMINEE:
      return 'Réception terminée — visible dans l’onglet Bon réception';
    case LC_STOCK_TRANSFERT_IS_RECEPTION.ANNULE:
      return 'Transfert annulé';
    default:
      return '';
  }
}

/** Libellé des onglets écran « Bon transfert ». */
export function getLcTransfertTabLabel(tab: LcStockTransfertTab): string {
  switch (tab) {
    case 'tous':
      return 'Tous';
    case 'en_cours':
      return 'Bon transfert';
    case 'livraison':
      return 'En circulation';
    case 'reception':
      return 'Bon réception';
    case 'clos':
      return 'Soldé';
    default:
      return tab;
  }
}

/** Clé normalisée pour classes CSS badge (sans accents). */
export function normalizeLcTransfertStatutKey(label: string): string {
  return String(label || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function getErpWorkflowDocumentTypeLabel(typeId: number): string | null {
  switch (Number(typeId)) {
    case STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_SORTIE:
      return 'Bon de sortie';
    case STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT:
      return 'Bon de transfert';
    case STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_RECEPTION:
      return 'Bon de réception';
    case STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART:
      return 'PV d\'écart';
    default:
      return null;
  }
}

/** Statuts ERP workflow transfert (documents). */
export const ERP_WORKFLOW_STATUT = {
  BROUILLON: 1,
  CREE: 9,
  EN_COURS: 16,
  TRANSFERE: 17,
  EN_CONTROLE: 22,
  RECEPTIONNE: 23,
  RECEPTION_PARTIELLE: 24,
  AVEC_ECART: 25,
} as const;

export type StockTransferConversionSource =
  | 'bon-transfert'
  | 'bon-sortie'
  | 'bon-reception';

/** Contexte ouverture modale conversion (transfert entrepôt LC + listes ERP). */
export type ConvertModalOpenContext = {
  /** Options imposées (ignore le calcul par statut). */
  forcedOptions?: string[];
  /** Onglet liste LC `/transfert-entrepot/...`. */
  lcTransfertTab?: 'en_cours' | 'livraison' | 'valide';
  lcTransfertId?: number;
  /** Liste `/stock/bon-transfert-doc/tous`. */
  bonTransfertDocList?: boolean;
};

export const CONVERT_OPTION = {
  BON_TRANSFERT: 'Bon de transfert',
  BON_LIVRAISON: 'Bon de livraison',
  BON_SORTIE: 'Bon de sortie',
  BON_RECEPTION: 'Bon de réception',
  EN_CIRCULATION: 'En circulation',
  PV_ECART: "PV d'écart",
} as const;

export function getLcTransfertTabForcedConversionOptions(
  tab: 'en_cours' | 'livraison' | 'reception' | 'tous' | 'clos' | string
): string[] | null {
  switch (tab) {
    case 'en_cours':
      /** Bon de transfert ERP déjà lié : sortie ou ouverture / suite sur le bon de transfert. */
      return [CONVERT_OPTION.BON_SORTIE, CONVERT_OPTION.BON_TRANSFERT];
    case 'livraison':
    case 'valide':
      /** Transfert LC validé → bon de sortie ERP (mise en circulation). */
      return [CONVERT_OPTION.BON_SORTIE];
    case 'reception':
      return null;
    default:
      return null;
  }
}

/** Cible de conversion LC (onglet liste ou code `is_reception` de la ligne). */
export function resolveLcTransfertForcedConversionOptions(
  tab: 'en_cours' | 'livraison' | 'reception' | 'tous' | 'clos' | string,
  isReceptionCode: unknown
): string[] | null {
  const fromTab = getLcTransfertTabForcedConversionOptions(tab);
  if (fromTab?.length) {
    return fromTab;
  }
  const code = Number(isReceptionCode);
  if (
    code === LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_TRANSFERT ||
    code === LC_STOCK_TRANSFERT_IS_RECEPTION.DEFAUT
  ) {
    return [CONVERT_OPTION.BON_SORTIE, CONVERT_OPTION.BON_TRANSFERT];
  }
  if (code === LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_LIVRAISON) {
    return [CONVERT_OPTION.BON_SORTIE];
  }
  return null;
}

function normalizeStatutKey(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Id racine du workflow (bon de transfert source). */
export function resolveTransferWorkflowRootId(doc: Record<string, unknown> | null | undefined): number | null {
  if (!doc) {
    return null;
  }
  const id = Number(doc['id']);
  const oidRaw = doc['document_original_id'];
  const oid = oidRaw != null && oidRaw !== '' ? Number(oidRaw) : NaN;
  const typeId = Number(
    doc['type_document_id'] ?? (doc['typeDocument'] as { id?: number })?.id
  );
  if (typeId === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT && Number.isFinite(id) && id > 0) {
    if (Number.isFinite(oid) && oid > 0 && oid !== id) {
      return oid;
    }
    return id;
  }
  if (Number.isFinite(oid) && oid > 0) {
    return oid;
  }
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Cibles de conversion autorisées (workflow transfert entrepôts).
 * Bon de sortie (liste vente) → bon de livraison.
 */
export function getStockTransferWorkflowConversionTargets(
  source: StockTransferConversionSource,
  statutId: number | null | undefined,
  statutTitre: string | null | undefined,
  options?: { bonTransfertDocList?: boolean }
): string[] {
  const sid = statutId != null ? Number(statutId) : NaN;
  const n = normalizeStatutKey(statutTitre);

  if (source === 'bon-transfert') {
    void options;
    void sid;
    void n;
    /** Bon de transfert ERP → bon de réception uniquement (PV d'écart via action dédiée). */
    return [CONVERT_OPTION.BON_RECEPTION];
  }

  if (source === 'bon-sortie') {
    return [CONVERT_OPTION.BON_LIVRAISON];
  }

  if (source === 'bon-reception') {
    const pvStatuts =
      sid === ERP_WORKFLOW_STATUT.AVEC_ECART ||
      sid === ERP_WORKFLOW_STATUT.RECEPTION_PARTIELLE ||
      sid === ERP_WORKFLOW_STATUT.EN_CONTROLE ||
      n.includes('ecart') ||
      n.includes('partielle') ||
      n.includes('controle');
    return pvStatuts ? ["PV d'écart"] : [];
  }

  return [];
}

export function getStockTransferConversionWorkflowStep(
  source: StockTransferConversionSource,
  targetLabel: string
): string | undefined {
  const target = normalizeStatutKey(targetLabel);
  if (target.includes('sortie')) {
    return 'BON_SORTIE';
  }
  if (target.includes('livraison')) {
    return 'BON_LIVRAISON';
  }
  if (target.includes('transfert')) {
    return 'BON_TRANSFERT';
  }
  if (target.includes('reception')) {
    return 'BON_RECEPTION';
  }
  if (target.includes('circulation')) {
    return 'EN_CIRCULATION';
  }
  if (target.includes('ecart')) {
    return 'PV_ECART';
  }
  return undefined;
}

/** Bouton convertir ERP : bon de transfert (Créé / Transféré / En cours) → bon de réception. */
export function isStockTransferWorkflowConvertAllowed(
  listType: string | undefined,
  statutId: number | null | undefined,
  statutTitre: string | null | undefined
): boolean {
  const t = String(listType || '').trim();
  const sid = statutId != null ? Number(statutId) : NaN;
  const n = normalizeStatutKey(statutTitre);

  if (t === 'bon-sortie') {
    if (sid === ERP_WORKFLOW_STATUT.BROUILLON || n === 'brouillon') {
      return false;
    }
    return true;
  }
  if (t === 'bon-transfert') {
    if (sid === ERP_WORKFLOW_STATUT.BROUILLON || n === 'brouillon') {
      return false;
    }
    return (
      sid === ERP_WORKFLOW_STATUT.CREE ||
      sid === ERP_WORKFLOW_STATUT.TRANSFERE ||
      sid === ERP_WORKFLOW_STATUT.EN_COURS ||
      n === 'cree' ||
      n === 'transfere' ||
      n === 'en cours'
    );
  }
  if (t === 'bon-reception') {
    if (sid === ERP_WORKFLOW_STATUT.BROUILLON || n === 'brouillon') {
      return false;
    }
    return true;
  }
  if (t === 'pv-ecart') {
    return false;
  }
  return false;
}
